/** ***********
*
* <select-family.component.ts v1.0>
*
* Copyright (c) 2022 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*  NG6C740 [thomas.pereira.external@airbus.com]
*
* All rights reserved.
**************/

import { ChangeDetectionStrategy, Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Validators, FormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ReplaySubject, Subscription } from 'rxjs';
import { SelectUserComponent } from '../select-user/select-user.component';
import { Family, ParentFamily, ParentFamilyRight } from 'src/app/models/family';
import { selectMyRightsTree$, selectUserRightLoading$ } from 'src/app/store/selectors/user-right.selectors';
import { Scope } from 'src/app/models/profile';
import { GroupFamily, RoleLevelEnum } from 'src/app/models/user-right';
import { take } from 'rxjs/operators';
import { FetchMyRightsTree } from 'src/app/store/actions/user-right.action';

type FamilyItem = Pick<Family, 'id' | 'name' | 'parentFamilyId' | 'scope'>;
type ParentFamilyItem = Pick<ParentFamily, 'id' | 'name' >;

export type FamilySelected = FamilyItem;

@Component({
    selector: 'app-select-family',
    templateUrl: './select-family.component.html',
    styleUrls: ['./select-family.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SelectFamilyComponent implements OnInit, OnDestroy {

  public familyForm = this._fb.group({
    parentFamilies: this._fb.control<ParentFamilyItem[]>([]),
    families: this._fb.control<FamilyItem[]>([], Validators.required)
  });
  public searchControls = this._fb.group({
    parentFamily: this._fb.control(''),
    family: this._fb.control(''),
  });
  public parentFamilies$: ReplaySubject<ParentFamilyItem[]> = new ReplaySubject(1);
  public families$: ReplaySubject<FamilyItem[]> = new ReplaySubject(1);
  public rightsLoading = false;

  private _subscription = new Subscription();
  private _families: FamilyItem[] = [];

  constructor(
    private _fb: FormBuilder,
    private store: Store,
    private _dialogRef: MatDialogRef<SelectUserComponent>,
    @Inject(MAT_DIALOG_DATA) private _data: GroupFamily[] = [],
  ) { }

  ngOnInit(): void {
    this._subscription.add(
      this.familyForm.get('parentFamilies').valueChanges.subscribe(
        (selection: ParentFamilyItem[]) => this._filterFamiliesOnParent(selection)
      )
    );
    this._subscription.add(
        this.store.select(selectUserRightLoading$).subscribe(
            loading => {
                this.rightsLoading = loading;
                if (loading) {
                    this.familyForm.disable();
                } else {
                    this.familyForm.enable();
                }
            }
        )
    );
    this._subscription.add(
        this.store.select(selectMyRightsTree$).subscribe(
            data => {
                const scopes = this._getScopesAvailable(data);
                const parentFamilies: ParentFamilyItem[] = [];
                const assetFamilies: FamilyItem[] = [];
                data.forEach(
                    ({ id, name, families, scope }) => {
                        families = families.filter(fam => !this._data?.some(family => family.id === fam.id));
                        if (!scopes.includes(scope as Scope)) {
                            return;
                        }
                        parentFamilies.push({ id, name });
                        if (families) {
                            assetFamilies.push(...families.filter(
                                family => family.level !== RoleLevelEnum.basic
                            ).map(
                                family => ({
                                    id: family.id,
                                    name: family.name,
                                    scope: family.scope as Scope,
                                    parentFamilyId: family.parentFamilyId,
                                })
                            ));
                        }
                    }
                );
                this.parentFamilies$.next([...parentFamilies]);
                this._families = [...assetFamilies];
                this.families$.next(this._families);
            }
        )
    );
    // Fetch user's rights
    this.store.dispatch(new FetchMyRightsTree());
  }

  /**
   * Get all the scopes available for the user given its rights
   * @param parentFamilies The parent families
   * @returns The scopes of the user
   */
  private _getScopesAvailable(parentFamilies: ParentFamilyRight[]): Scope[] {
    const allScopes = Object.values(Scope).filter(
      v => isNaN(+v)
    );
    const scopes: Scope[] = [];
    for (const parentFamily of parentFamilies) {
        // Skip if no right or no scope
      if (parentFamily.level === RoleLevelEnum.basic || !parentFamily.scope) {
        continue;
      }
      const parentScope = parentFamily.scope as Scope;
      if (!scopes.includes(parentScope)) {
        scopes.push(parentScope);
      }
      if (allScopes.every(scope => scopes.includes(scope))) {
        break;
      }
    }
    return scopes;
  }

  /**
   * Filter the users with the searched text
   */
  private _filterFamiliesOnParent(parents: ParentFamilyItem[]): void {
    if (!parents?.length) {
      this.families$.next([...this._families]);
    } else {
      this.families$.next([...this._families.filter(
        family => parents.find(
          parent => parent.id === family.parentFamilyId
        )
      )]);
    }
  }

  /**
   * Close the dialog
   */
  public onClickCancel(): void {
    this._dialogRef.close();
  }

  /**
   * Close the dialog & pass the selected users
   */
  public onFormSubmit(): void {
    this._dialogRef.close([...this.familyForm.value.families]);
  }

  /**
   * Triggered when the select all checkbox is clicked
   * @param selected The checkbox selection state
   */
  public toggleAllFamilies(selected: boolean): void {
    const familiesFormControl = this.familyForm.get('families');
    if (selected) {
      this.families$.pipe(
        take(1),
      ).subscribe(
        families => {
          const searchedValue = this.searchControls.get('family').value;
          const searched = new RegExp(searchedValue ?? '', 'i');
          familiesFormControl.setValue(families.filter(
            ({ name }) => searched.test(name)
          ));
        }
      );
    } else {
      familiesFormControl.setValue([]);
    }
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

}
